import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, isValid } from 'date-fns';
import { Loader2, PackageOpen } from 'lucide-react';
import { posApi } from '@/services/api';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import ReportExportButton from '@/components/reports/ReportExportButton';
import { formatMoney } from '@/utils/currency';

const formatDateSafe = (dateStr: string | null | undefined) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return isValid(d) ? format(d, 'dd MMM yyyy') : '—';
};

export default function DamageLossSection() {
  const {
    dateRange,
    setDateRange,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    from_date,
    to_date,
  } = useReportDateRange('This Month');

  const [category, setCategory] = useState('');

  const { data: categoriesData } = useQuery({
    queryKey: ['categories', 'Product'],
    queryFn: async () => {
      const res = await posApi.getCategories({ category_type: 'Product' });
      return res.data.message || [];
    },
  });

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'damageLoss',
      from_date ? String(from_date) : '',
      to_date ? String(to_date) : '',
      category,
    ],
    queryFn: async () => {
      const res = await posApi.getDamageLossReport({
        from_date: String(from_date),
        to_date: String(to_date),
        category: category || undefined,
      });
      return res.data.message as {
        rows: any[];
        summary: {
          entry_count: number;
          total_qty: number;
          total_loss_value: number;
        };
      };
    },
    enabled: Boolean(from_date && to_date),
  });

  const rows = data?.rows || [];
  const summary = data?.summary;

  const handleExport = () => {
    if (!rows.length) return;
    const headers = [
      'Date',
      'Stock Entry',
      'Item Code',
      'Item Name',
      'Variant',
      'Category',
      'Qty',
      'Unit Cost',
      'Loss Value',
      'Recorded By',
    ];
    const csvRows = rows.map((r) =>
      [
        r.transaction_date || '',
        r.stock_entry || '',
        r.item_code || '',
        `"${(r.item_name || '').replace(/"/g, '""')}"`,
        `"${(r.variant_name || '').replace(/"/g, '""')}"`,
        `"${(r.category_name || '').replace(/"/g, '""')}"`,
        r.quantity,
        r.unit_price,
        r.loss_value,
        r.recorded_by || '',
      ].join(','),
    );
    const blob = new Blob([[headers.join(','), ...csvRows].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `damage_loss_${from_date}_to_${to_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="card p-4">
          <div className="text-xs uppercase tracking-wide text-gray-400">Entries</div>
          <div className="text-xl font-bold text-gray-900 mt-1">{summary?.entry_count ?? '—'}</div>
        </div>
        <div className="card p-4">
          <div className="text-xs uppercase tracking-wide text-gray-400">Total qty lost</div>
          <div className="text-xl font-bold text-gray-900 mt-1">
            {summary ? Number(summary.total_qty).toLocaleString() : '—'}
          </div>
        </div>
        <div className="card p-4">
          <div className="text-xs uppercase tracking-wide text-gray-400">Loss value</div>
          <div className="text-xl font-bold text-rose-700 mt-1">
            {summary ? formatMoney(summary.total_loss_value) : '—'}
          </div>
        </div>
      </div>

      <div className="card p-4 flex flex-col xl:flex-row xl:items-end gap-3 justify-between">
        <ReportDateRangeFilter
          dateRange={dateRange}
          onDateRangeChange={setDateRange}
          customFrom={customFrom}
          onCustomFromChange={setCustomFrom}
          customTo={customTo}
          onCustomToChange={setCustomTo}
        />
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[180px]">
            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
            <select className="input-field" value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">All categories</option>
              {(categoriesData || []).map((c: any) => (
                <option key={c.name} value={c.name}>
                  {c.category_name || c.name}
                </option>
              ))}
            </select>
          </div>
          <ReportExportButton onClick={handleExport} disabled={!rows.length} />
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
          Failed to load damage / loss report.
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap min-w-[960px]">
            <thead className="bg-gray-50 border-b border-gray-100 text-gray-600">
              <tr>
                <th className="px-4 py-3 font-medium">Date</th>
                <th className="px-4 py-3 font-medium">Item</th>
                <th className="px-4 py-3 font-medium">Variant</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium text-right">Qty</th>
                <th className="px-4 py-3 font-medium text-right">Unit cost</th>
                <th className="px-4 py-3 font-medium text-right">Loss value</th>
                <th className="px-4 py-3 font-medium">Entry</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading damage / loss…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                    <PackageOpen className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                    No damage / loss stock outs for this filter.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.stock_entry} className="hover:bg-gray-50/60">
                    <td className="px-4 py-3 text-gray-700">{formatDateSafe(r.transaction_date)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{r.item_name}</div>
                      <div className="text-xs text-gray-500 font-mono">{r.item_code}</div>
                    </td>
                    <td className="px-4 py-3">{r.variant_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{r.category_name || '—'}</td>
                    <td className="px-4 py-3 text-right font-semibold">{r.quantity}</td>
                    <td className="px-4 py-3 text-right">{formatMoney(r.unit_price)}</td>
                    <td className="px-4 py-3 text-right font-medium text-rose-700">
                      {formatMoney(r.loss_value)}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">{r.stock_entry}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
